import { Author } from '../../../api/Author';
import { Recipe } from '../../../api/Recipe';

export type RecipePageToastType = 'success' | 'error' | 'info';

export interface EditRecipePageState {
  isAuthor: boolean;
  isRecipeFavorite: boolean;
}

interface ShareRecipePageParams {
  recipe: Recipe | null;
  buildPublicRecipeUrl: (recipeId: string) => string;
  shareRecipe: (recipe: Recipe, publicRecipeUrl: string) => Promise<void>;
  copyRecipeLink: (publicRecipeUrl: string) => Promise<void>;
  onShareError: (error: unknown) => void;
  onCopyError: (error: unknown) => void;
  supportsNativeShare: boolean;
}

interface RefreshRecipeAfterUpdateParams {
  recipeId?: string;
  reloadRecipe: (errorPrefix?: string) => Promise<Recipe | null>;
  navigateHome: () => void;
  stopEditing: () => void;
}

interface ToggleRecipeFavoriteParams {
  recipe: Recipe | null;
  toggleFavorite: (recipe: Recipe) => Promise<void>;
  showToast: (message: string, type?: RecipePageToastType) => void;
  getErrorMessage: (error: unknown) => string;
}

export const deriveEditRecipePageState = (
  author: Author | null,
  recipe: Recipe | null,
  isFavorite: (recipeId: string) => boolean
): EditRecipePageState => ({
  isAuthor: Boolean(author && recipe && author.id === recipe.author_id),
  isRecipeFavorite: Boolean(recipe && isFavorite(recipe.id)),
});

export const shareRecipePage = async ({
  recipe,
  buildPublicRecipeUrl,
  shareRecipe,
  copyRecipeLink,
  onShareError,
  onCopyError,
  supportsNativeShare,
}: ShareRecipePageParams): Promise<void> => {
  if (!recipe) {
    return;
  }

  const publicRecipeUrl = buildPublicRecipeUrl(recipe.id);

  if (supportsNativeShare) {
    try {
      await shareRecipe(recipe, publicRecipeUrl);
    } catch (error) {
      onShareError(error);
    }
    return;
  }

  try {
    await copyRecipeLink(publicRecipeUrl);
  } catch (error) {
    onCopyError(error);
  }
};

export const refreshRecipeAfterUpdate = async ({
  recipeId,
  reloadRecipe,
  navigateHome,
  stopEditing,
}: RefreshRecipeAfterUpdateParams): Promise<void> => {
  if (!recipeId) {
    navigateHome();
    return;
  }

  try {
    const refreshedRecipe = await reloadRecipe(
      'Erreur lors du rechargement de la recette après mise à jour'
    );

    if (refreshedRecipe) {
      stopEditing();
    }
  } catch {
    // reloadRecipe already exposes the error state consumed by the page
  }
};

export const navigateAfterRecipeDeletion = (navigateHome: () => void) => {
  navigateHome();
};

export const toggleRecipeFavoriteForPage = async ({
  recipe,
  toggleFavorite,
  showToast,
  getErrorMessage,
}: ToggleRecipeFavoriteParams): Promise<void> => {
  if (!recipe) {
    return;
  }

  try {
    await toggleFavorite(recipe);
  } catch (error) {
    showToast(getErrorMessage(error), 'error');
  }
};
